/**
 * Type definitions.
 * 
 * @typedef {Object.<string, Function[]>} BlockLoadHandlers
 */


import initialiseAlpineComponents from "./gute/alpine";
import { initialiseAllSliders } from "./components/sliders";

import "./gute/scrollspy-block";
import "./gute/reeller";
import "./gute/sliders";

import $ from "jquery";
import Alpine from "alpinejs";

/**
 * Wrapping class for the Gutenberg & generic component functionality.
 */
new class Gute {
    /**
     * List of blocks that we've already handled creation events for.
     * 
     * @var {{}[]}
     */
    knownBlocks = null;

    /**
     * Object for storing the Gutenberg block loading handlers, keys is the block name and value is a list of functions to execute on load.
     * 
     * @var {BlockLoadHandlers}
     */
    blockLoadHandlers = {};

    /**
     * Constructor
     */
    constructor() { 
        initialiseAlpineComponents()
            .then( this.postInitialiseComponents.bind( this ) )
            .then( this.postInitialiseAlpine.bind( this ) );
    }

    /**
     * Called when the alpine component definitions have been injected into the window.
     */
    postInitialiseComponents() {
        window[ 'Alpine' ] = Alpine;
        Alpine.start();
    }

    /**
     * Called once Alpine.js has had `.start()` called on it, this is where we begin listening for window events.
     */
    postInitialiseAlpine() {
        $( this.postDOMReady.bind( this ) );
    }

    /**
     * Called when the window is ready.
     */
    postDOMReady() { 
        // If we're in the backend block editor.
        if ( window[ 'wp' ] && window[ 'wp' ][ 'blocks' ] ) {
            this.initialiseGutenbergEditor();
        } else { 
            this.initialiseFrontend();
        }
    }

    /**
     * Called when we are certain we are not on the Gutenberg editor, and are anyone viewing or previewing the frontend.
     */
    initialiseFrontend() {
        initialiseAllSliders();
    }

    /**
     * Called when we are certain we're on the WordPress Gutenberg editor and not the frontend.
     */
    initialiseGutenbergEditor() {
        this.knownBlocks = wp.data
            .select( 'core/block-editor' )
            .getBlocks()
            .map( block => block.clientId );

        // Subscribe to the builtin wordpress data stream, this is so we can catch update events on builtin or non-acf block types.
        window.wp.data.subscribe( this.onWordpressDataEvent.bind( this ) );

        // And if ACF is loaded (with our block autoloader) then we can hook into the `render_block_preview/type={block_name}` action on each of the blocks.
        if ( window[ 'acf' ] && window[ 'ifg_block_names' ] ) {
            const iter = ( 
                function ( blockName ) { 
                    // When the block of `blockName` is updated
                    window.acf.addAction( 
                        `render_block_preview/type=${ blockName.replace( 'acf/', '' ) }`, 
                        this.onBlockUpdate.bind( this, { name: blockName } ) // call me
                    );
                } 
            ).bind( this );

            window[ 'ifg_block_names' ].forEach( iter );
        }
    }

    /**
     * Called on WordPress data update event.
     */
    onWordpressDataEvent() { 
        const blockEditorData   = wp.data.select( 'core/block-editor' );
        const currentBlocks     = blockEditorData.getBlocks().map( block => block.clientId );
        const newBlockId        = currentBlocks.find( id => ! this.knownBlocks.includes( id ) );

        if ( newBlockId ) {
            const newBlock = blockEditorData.getBlock( newBlockId );

            this.onBlockUpdate( newBlock );
            this.knownBlocks.push( newBlockId );
        }
    }

    /**
     * Called on the event a block is either inserted or updated within the Gutenberg editor.
     * 
     * @param {{
     *      attributes: {
     *          align:              String,
     *          data:               {},
     *          mode:               String,
     *          name:               String,
     *          _acf_context:       {postId:Number,postType:String}|null
     *      },
     *      clientId:           String,
     *      innerBlocks:        [],
     *      isValid:            Boolean,
     *      name:               String,
     *      originalContent:    String,
     *      validationIssues:   []
     * }} block 
     */
    onBlockUpdate( block ) { 
        switch ( block.name ) {
        case "acf/testimonials-slider-block":
            initialiseAllSliders();
            break;
        }
    }

    /**
     * Wrapper for console.log that only applies when WP_DEBUG is activated.
     * 
     * @param  {...any} _ 
     */
    log( ..._ ) {
        if ( ! window[ 'WP_DEBUG' ] ) {
            return;
        }

        console.log( ...arguments );
    }
};
